import { createContext, lazy, ReactNode, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'

import { ChatConversation, chatBotPatch, countsAsUnread, getChatConversation, listChatConversations, messagePreview } from '@/lib/chatCore'
import {
  addSeen,
  handoffAlertFrom,
  handoffMuteKey,
  handoffSeenKey,
  mutedUntil,
  muteUntil,
  parseSeen,
  shouldAlertHandoff,
  type HandoffAlert,
  type HandoffMuteMinutes
} from '@/lib/handoffAlert'
import { AuthContext } from '@/providers/auth'
import { useChatSocket } from '@/pages/Chat/useChatSocket'
import { useMyAccess } from '@/hooks/useMyAccess'
import { grants } from '@/lib/access'

const HandoffAlertDialog = lazy(() =>
  import('@/pages/Chat/HandoffAlertDialog').then(m => ({ default: m.HandoffAlertDialog })).catch(() => ({ default: () => <></> }))
)

const NOTIFICATION_SOUND = '/chat-notification.mp3'
const NOTIFICATION_ICON = '/icon-192x192.png'

type ChatNotificationsContext = {
  total: number
  conversations: ChatConversation[]
}

const Ctx = createContext<ChatNotificationsContext>({ total: 0, conversations: [] })

export function useChatNotifications() {
  return useContext(Ctx)
}

function useTabVisible() {
  const [visible, setVisible] = useState(typeof document !== 'undefined' ? document.visibilityState === 'visible' : true)
  useEffect(() => {
    const handler = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])
  return visible
}

function supportsWebNotifications(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

function readStorage(key: string | null): string | null {
  if (!key || typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string | null, value: string) {
  if (!key || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, value)
  } catch {}
}

export function ChatNotificationsProvider({ children }: { children: ReactNode }) {
  const { user, company } = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()
  const tabVisible = useTabVisible()

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isFirstLoadRef = useRef(true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const audio = new Audio(NOTIFICATION_SOUND)
    audio.preload = 'auto'
    audioRef.current = audio

    let unlocked = false
    const requestPermissionOnce = () => {
      if (!supportsWebNotifications()) return
      if (Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {})
      }
    }
    const unlock = () => {
      if (unlocked) return
      const a = audioRef.current
      if (!a) return
      a.muted = true
      a.play()
        .then(() => {
          a.pause()
          a.currentTime = 0
          a.muted = false
          unlocked = true
          requestPermissionOnce()
          window.removeEventListener('click', unlock)
          window.removeEventListener('touchend', unlock)
          window.removeEventListener('keydown', unlock)
        })
        .catch(() => {
          a.muted = false
          requestPermissionOnce()
        })
    }

    window.addEventListener('click', unlock)
    window.addEventListener('touchend', unlock)
    window.addEventListener('keydown', unlock)

    return () => {
      window.removeEventListener('click', unlock)
      window.removeEventListener('touchend', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [])

  const playSound = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    try {
      audio.currentTime = 0
      audio.play().catch(() => {})
    } catch {}
  }, [])

  const showWebNotification = useCallback(
    (arrival: ChatConversation) => {
      if (!supportsWebNotifications()) return
      if (Notification.permission !== 'granted') return
      if (typeof document !== 'undefined' && !document.hidden) return
      try {
        const notif = new Notification(arrival.visitorName || 'Nova mensagem', {
          body: arrival.lastMessagePreview ?? 'Nova mensagem no chat',
          icon: NOTIFICATION_ICON,
          tag: `chat-${arrival.id}`
        })
        notif.onclick = () => {
          window.focus()
          navigate(`/chat/${arrival.id}`)
          notif.close()
        }
      } catch {}
    },
    [navigate]
  )

  const { data: access } = useMyAccess()
  const membership = company?.id ? access?.memberships?.find(item => item.company.id === company.id) : undefined
  const canReadChat = !!access?.isSuperAdmin || grants(membership?.permissions, 'chat', 'read')
  const enabled = !!user?.id && !!company?.id && canReadChat

  const { data, refetch } = useQuery(['chat-notifications', company?.id], () => listChatConversations({ perPage: 100 }), { enabled })

  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const conversationsRef = useRef(conversations)
  conversationsRef.current = conversations
  const seenMessagesRef = useRef(new Set<string>())
  const [handoffs, setHandoffs] = useState<HandoffAlert[]>([])
  const [handoffDialogNeeded, setHandoffDialogNeeded] = useState(false)
  if (handoffs.length > 0 && !handoffDialogNeeded) setHandoffDialogNeeded(true)
  const raisedHandoffsRef = useRef(new Set<string>())

  const muteKey = user?.id && company?.id ? handoffMuteKey(user.id, company.id) : null
  const seenKey = user?.id && company?.id ? handoffSeenKey(user.id, company.id) : null

  useEffect(() => {
    setConversations([])
    setHandoffs([])
    raisedHandoffsRef.current = new Set()
    isFirstLoadRef.current = true
  }, [company?.id])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (event: StorageEvent) => {
      if (event.key === muteKey && mutedUntil(event.newValue) !== null) setHandoffs([])
      if (event.key === seenKey) {
        const seen = parseSeen(event.newValue)
        setHandoffs(prev => prev.filter(alert => !seen.includes(alert.key)))
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [muteKey, seenKey])

  useEffect(() => {
    if (!data || !enabled) return
    setConversations(data.rows ?? [])
    isFirstLoadRef.current = false
  }, [data, enabled])

  const onChatPathFor = useCallback((id: string) => location.pathname === `/chat/${id}`, [location.pathname])

  const notifyArrival = useCallback(
    (arrival: ChatConversation) => {
      if (isFirstLoadRef.current) return
      if (onChatPathFor(arrival.id) && tabVisible) return
      if (tabVisible) {
        playSound()
        toast.info(`${arrival.visitorName}: ${arrival.lastMessagePreview ?? 'nova mensagem'}`, {
          action: {
            label: 'Abrir',
            onClick: () => navigate(`/chat/${arrival.id}`)
          }
        })
      }
      showWebNotification(arrival)
    },
    [onChatPathFor, tabVisible, playSound, navigate, showWebNotification]
  )

  const showHandoffNotification = useCallback((alert: HandoffAlert) => {
    if (!supportsWebNotifications()) return
    if (Notification.permission !== 'granted') return
    if (typeof document !== 'undefined' && !document.hidden) return
    try {
      const notif = new Notification('A IA está pedindo ajuda de um corretor', {
        body: [alert.conversation?.visitorName, alert.summary].filter(Boolean).join(': ') || 'Um cliente está esperando um corretor',
        icon: NOTIFICATION_ICON,
        tag: `handoff-${alert.conversationId}`,
        requireInteraction: true
      })
      notif.onclick = () => {
        window.focus()
        notif.close()
      }
    } catch {}
  }, [])

  const raiseHandoff = useCallback(
    async (alert: HandoffAlert) => {
      if (!user?.id || raisedHandoffsRef.current.has(alert.key)) return
      raisedHandoffsRef.current.add(alert.key)

      const known = conversations.find(c => c.id === alert.conversationId)
      const conversation = known ?? (await getChatConversation(alert.conversationId).catch(() => undefined))
      const withConversation = { ...alert, conversation }
      const allowed = shouldAlertHandoff(withConversation, {
        userId: user.id,
        viewingConversationId: onChatPathFor(alert.conversationId) && tabVisible ? alert.conversationId : null,
        mutedUntil: mutedUntil(readStorage(muteKey)),
        seen: parseSeen(readStorage(seenKey))
      })
      if (!allowed) return

      setHandoffs(prev => [withConversation, ...prev.filter(item => item.conversationId !== alert.conversationId)])
      playSound()
      showHandoffNotification(withConversation)
    },
    [user?.id, conversations, onChatPathFor, tabVisible, muteKey, seenKey, playSound, showHandoffNotification]
  )

  const dismissHandoffs = useCallback(
    (muteMinutes: HandoffMuteMinutes | null) => {
      writeStorage(
        seenKey,
        JSON.stringify(
          addSeen(
            parseSeen(readStorage(seenKey)),
            handoffs.map(alert => alert.key)
          )
        )
      )
      if (muteMinutes) writeStorage(muteKey, String(muteUntil(muteMinutes)))
      setHandoffs([])
    },
    [handoffs, muteKey, seenKey]
  )

  const openHandoffConversation = useCallback(
    (conversationId: string, muteMinutes: HandoffMuteMinutes | null) => {
      dismissHandoffs(muteMinutes)
      navigate(`/chat/${conversationId}`)
    },
    [dismissHandoffs, navigate]
  )

  useChatSocket({
    companyId: enabled ? company?.id : undefined,
    onResync: () => {
      refetch()
    },
    onConversationCreated: (payload: any) => {
      const { conversationId, conversation } = payload ?? {}
      const idKey = conversationId ?? conversation?.id
      if (!idKey || !conversation) return
      const incoming = { ...(conversation as Partial<ChatConversation>), id: idKey } as ChatConversation
      setConversations(prev => (prev.some(c => c.id === idKey) ? prev : [incoming, ...prev]))
    },
    onMessage: (payload: any) => {
      const { conversationId, message } = payload ?? {}
      if (!conversationId || !message) return
      if (typeof message.id === 'string') {
        if (seenMessagesRef.current.has(message.id)) return
        seenMessagesRef.current.add(message.id)
        if (seenMessagesRef.current.size > 500) seenMessagesRef.current = new Set([...seenMessagesRef.current].slice(-250))
      }
      const fromVisitor = countsAsUnread(message.sender)
      const viewingHere = onChatPathFor(conversationId) && tabVisible
      const preview = messagePreview(message) || undefined
      const known = conversationsRef.current.find(c => c.id === conversationId)

      if (!known) refetch()
      else
        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === conversationId)
          if (idx === -1) return prev
          const current = prev[idx]
          const updated: ChatConversation = {
            ...current,
            lastMessageAt: message.createdAt ?? current.lastMessageAt,
            lastMessagePreview: preview ?? current.lastMessagePreview,
            unreadByAgent: fromVisitor && !viewingHere ? (current.unreadByAgent ?? 0) + 1 : (current.unreadByAgent ?? 0)
          }
          const next = [...prev]
          next.splice(idx, 1)
          return [updated, ...next]
        })

      if (fromVisitor && !viewingHere) {
        const arrival: ChatConversation = {
          ...(known ?? ({ id: conversationId } as ChatConversation)),
          visitorName: message.authorName ?? known?.visitorName ?? 'Nova mensagem',
          lastMessagePreview: preview ?? known?.lastMessagePreview
        }
        notifyArrival(arrival)
      }
    },
    onConversationBot: (payload: any) => {
      const conversationId = payload?.conversationId
      if (!conversationId) return
      const patch = chatBotPatch(payload)
      setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, ...patch } : c)))

      const alert = handoffAlertFrom(payload)
      if (alert) {
        void raiseHandoff(alert)
        return
      }
      setHandoffs(prev => prev.filter(item => item.conversationId !== conversationId))
    },
    onConversationRead: (payload: any) => {
      const conversationId = payload?.conversationId
      if (!conversationId) return
      setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, unreadByAgent: 0 } : c)))
    }
  })

  const value = useMemo<ChatNotificationsContext>(() => {
    const total = conversations.reduce((acc, c) => acc + ((c.unreadByAgent ?? 0) > 0 && !c.closed ? 1 : 0), 0)
    return { total, conversations }
  }, [conversations])

  return (
    <Ctx.Provider value={value}>
      {children}
      {handoffDialogNeeded && (
        <Suspense fallback={null}>
          <HandoffAlertDialog alerts={handoffs} onOpenConversation={openHandoffConversation} onClose={dismissHandoffs} />
        </Suspense>
      )}
    </Ctx.Provider>
  )
}
